import { useTranslation } from 'react-i18next';

import { formatSeconds } from '../../utils/workoutStats';

// Helpers the list, the open workout, the editor and the library share

// The warm-up suffix is part of the saved name ("Supino Reto (Aquecimento)"); it shows as a tag instead
const WARMUP_SUFFIX = /\s*\((warmup|aquecimento)\)/gi;
export const baseName = (exercise) =>
  (exercise?.name || '').replace(WARMUP_SUFFIX, '').trim();

const hasLoad = (exercise) =>
  parseFloat(String(exercise?.weight ?? '').replace(',', '.')) > 0;

/** "40 kg", "12,5 kg", "45 lb"; empty when there's no load */
export const formatLoad = (exercise, language) => {
  if (!hasLoad(exercise)) return '';
  const value = String(exercise.weight).replace(',', '.');
  const shown = language === 'pt' ? value.replace('.', ',') : value;
  return `${shown} ${exercise.weightUnit === 'lbs' ? 'lb' : 'kg'}`;
};

/** "4 × 10 · 40 kg · descanso 1:30" */
export const useExerciseLine = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  return (exercise, { rest = true } = {}) =>
    [
      `${exercise.series || 0} × ${exercise.reps || 0}`,
      formatLoad(exercise, language),
      rest &&
        t('pages.Workout.editor.restLine', {
          time: formatSeconds(Number(exercise.interval) || 60),
        }),
    ]
      .filter(Boolean)
      .join(' · ');
};

/** Sets across the workout */
export const countSets = (exercises) =>
  (exercises || []).reduce(
    (total, exercise) => total + (parseInt(exercise.series) || 0),
    0
  );

const normalize = (text) =>
  text?.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '') ?? '';

/** The library entry's name in the site's language */
export const libraryTitle = (attrs, isPt) =>
  isPt && attrs.titlePt ? attrs.titlePt : attrs.title || '';

/** What an exercise takes from its library entry: the name as the library writes it, the steps and the media */
export const libraryFields = (attrs, isPt) => {
  const instructions = attrs.localisedInstructions || {};
  return {
    name: libraryTitle(attrs, isPt),
    howTo: isPt
      ? instructions.pt || instructions.en || ''
      : instructions.en || '',
    videoUrl: attrs.videoUrl || '',
    thumbnailUrl: attrs.thumbnailUrl || '',
    gifUrl: '',
    exerciseId: attrs.exerciseId || '',
  };
};

/**
 * Library search: entries with art, matching the name in either language; names that start with the search come
 * first, then the most used (the library's own priority), then A–Z
 */
export const searchLibrary = (
  exercises,
  { query = '', regionOf, region = '', isPt }
) => {
  const search = normalize(query.trim());
  const starts = (attrs) =>
    normalize(libraryTitle(attrs, isPt)).startsWith(search) ? 0 : 1;
  return exercises
    .filter(({ attributes: attrs }) => {
      if (!attrs || !(attrs.videoUrl || attrs.thumbnailUrl)) return false;
      if (
        search &&
        !normalize(attrs.title).includes(search) &&
        !normalize(attrs.titlePt).includes(search)
      )
        return false;
      return !region || regionOf(attrs.muscleGroup) === region;
    })
    .sort(
      (a, b) =>
        (search ? starts(a.attributes) - starts(b.attributes) : 0) ||
        (b.attributes.priority || 0) - (a.attributes.priority || 0) ||
        libraryTitle(a.attributes, isPt).localeCompare(
          libraryTitle(b.attributes, isPt),
          isPt ? 'pt' : 'en'
        )
    );
};
